import React, { useEffect, useRef } from 'react';

export type ReactionType = 'like' | 'love' | 'haha' | 'wow' | 'sad' | 'angry';

interface ReactionPickerProps {
  isVisible: boolean;
  onReactionSelect: (reaction: ReactionType) => void;
  onClose: () => void;
  position?: { x: number; y: number };
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const reactions: { 
  type: ReactionType; 
  emoji: string; 
  label: string; 
  bgGradient: string; 
  shadowColor: string;
}[] = [
  { 
    type: 'like', 
    emoji: '👍', 
    label: 'Thích', 
    bgGradient: 'from-blue-400 to-blue-600',
    shadowColor: 'shadow-blue-500/50'
  },
  { 
    type: 'love', 
    emoji: '❤️', 
    label: 'Yêu thích', 
    bgGradient: 'from-red-400 to-pink-600',
    shadowColor: 'shadow-red-500/50'
  },
  { 
    type: 'haha', 
    emoji: '😆', 
    label: 'Haha', 
    bgGradient: 'from-yellow-400 to-orange-500',
    shadowColor: 'shadow-yellow-500/50'
  },
  { 
    type: 'wow', 
    emoji: '😮', 
    label: 'Wow', 
    bgGradient: 'from-orange-400 to-red-500',
    shadowColor: 'shadow-orange-500/50'
  },
  { 
    type: 'sad', 
    emoji: '😢', 
    label: 'Buồn', 
    bgGradient: 'from-blue-400 to-indigo-600',
    shadowColor: 'shadow-blue-400/50'
  },
  { 
    type: 'angry', 
    emoji: '😠', 
    label: 'Phẫn nộ', 
    bgGradient: 'from-red-600 to-red-800',
    shadowColor: 'shadow-red-600/50'
  }
];

export const ReactionPicker: React.FC<ReactionPickerProps> = ({ 
  isVisible, 
  onReactionSelect, 
  onClose, 
  position,
  onMouseEnter,
  onMouseLeave
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const [hideTimeout, setHideTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [isHovering, setIsHovering] = React.useState(false);

  // Clear any existing timeout
  const clearHideTimeout = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
  };

  // Handle click outside - close immediately
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        if (hideTimeout) {
          clearTimeout(hideTimeout);
        }
      };
    }
  }, [isVisible, onClose, hideTimeout]);

  // Handle mouse enter - cancel any close timeout
  const handleMouseEnter = () => {
    setIsHovering(true);
    clearHideTimeout();
    onMouseEnter?.();
  };

  // Handle mouse leave - delegate to parent component
  const handleMouseLeave = () => {
    setIsHovering(false);
    clearHideTimeout(); // Clear any existing timeout
    onMouseLeave?.(); // Let parent handle the close logic
  };

  if (!isVisible) return null;

  const pickerStyle = position 
    ? { 
        position: 'fixed' as const,
        left: position.x, 
        top: position.y,
        zIndex: 9999 
      }
    : {};

  return (
    <div
      ref={pickerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="reaction-picker-container animate-bounce-in"
      style={pickerStyle}
    >
      {/* Main picker with glassmorphism effect */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 p-2 flex items-center gap-1 before:absolute before:inset-0 before:bg-gradient-to-r before:from-blue-50/30 before:via-purple-50/30 before:to-pink-50/30 before:rounded-2xl before:-z-10">
        {reactions.map((reaction, index) => (
          <button
            key={reaction.type}
            onClick={() => onReactionSelect(reaction.type)}
            className="reaction-button relative group"
            title={reaction.label}
            style={{
              animationDelay: `${index * 50}ms`
            }}
          >
            {/* Gradient background on hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${reaction.bgGradient} opacity-0 group-hover:opacity-20 rounded-full transition-all duration-300 blur-sm scale-110`}></div>
            
            {/* Emoji */}
            <span className="relative text-3xl filter drop-shadow-lg transition-all duration-300 group-hover:scale-125 group-active:scale-95 emoji-bounce">
              {reaction.emoji}
            </span>
            
            {/* Professional Tooltip with gradient */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none tooltip-slide-up">
              <div className={`px-4 py-2 bg-gradient-to-r ${reaction.bgGradient} text-white text-sm font-medium rounded-xl shadow-xl whitespace-nowrap relative`}>
                {reaction.label}
                {/* Arrow */}
                <div className={`absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-transparent border-t-white`}></div>
              </div>
            </div>

            {/* Ripple effect on click */}
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className={`ripple bg-gradient-to-r ${reaction.bgGradient}`}></div>
            </div>
          </button>
        ))}
      </div>

      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-pink-400/20 rounded-2xl blur-xl -z-20 animate-pulse"></div>
    </div>
  );
};

// Professional CSS animations and styles
const styles = `
  /* Main container bounce-in animation */
  @keyframes bounce-in {
    0% {
      transform: scale(0.5) translateY(30px);
      opacity: 0;
    }
    60% {
      transform: scale(1.08) translateY(-8px);
      opacity: 0.9;
    }
    80% {
      transform: scale(0.95) translateY(2px);
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }
  
  .animate-bounce-in {
    animation: bounce-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  /* Reaction picker container */
  .reaction-picker-container {
    position: relative;
    filter: drop-shadow(0 10px 40px rgba(0, 0, 0, 0.15));
  }
  
  /* Individual reaction button */
  .reaction-button {
    position: relative;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
    animation: button-appear 0.4s ease-out forwards;
    opacity: 0;
    transform: scale(0.5);
  }

  @keyframes button-appear {
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .reaction-button:hover {
    transform: translateY(-8px) scale(1.15);
    filter: brightness(1.1);
  }

  .reaction-button:active {
    transform: translateY(-4px) scale(1.05);
  }
  
  /* Emoji bounce effect */
  .emoji-bounce {
    display: inline-block;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .reaction-button:hover .emoji-bounce {
    animation: emoji-wiggle 0.6s ease-in-out infinite;
  }

  @keyframes emoji-wiggle {
    0%, 100% { transform: rotate(0deg) scale(1); }
    25% { transform: rotate(-10deg) scale(1.1); }
    75% { transform: rotate(10deg) scale(1.1); }
  }
  
  /* Tooltip slide-up animation */
  .tooltip-slide-up {
    animation: slide-up 0.3s ease-out;
  }

  @keyframes slide-up {
    from {
      transform: translate(-50%, 10px);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }
  
  /* Ripple effect on click */
  .ripple {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    opacity: 0;
    transform: scale(0);
  }

  .reaction-button:active .ripple {
    animation: ripple-effect 0.6s ease-out;
  }

  @keyframes ripple-effect {
    0% {
      transform: scale(0);
      opacity: 0.6;
    }
    100% {
      transform: scale(2.5);
      opacity: 0;
    }
  }

  /* Glow pulse effect */
  @keyframes glow-pulse {
    0%, 100% {
      opacity: 0.3;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.05);
    }
  }

  .animate-pulse {
    animation: glow-pulse 3s ease-in-out infinite;
  }

  /* Glassmorphism enhancement */
  .reaction-picker-container::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 1rem;
    padding: 2px;
    background: linear-gradient(135deg, 
      rgba(59, 130, 246, 0.3),
      rgba(147, 51, 234, 0.3),
      rgba(236, 72, 153, 0.3)
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s;
  }

  .reaction-picker-container:hover::before {
    opacity: 1;
  }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('reaction-picker-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'reaction-picker-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
